import React from "react";
import { Box, TableCell, TableHead, Typography, Tooltip } from "@mui/material";
import { muiTheme } from "@/styles/theme";
import { TableHeaderProps } from "@/modules/common/components/tables/common-table/body/types";
import FilterRenderer from "@/modules/common/components/tables/common-table/body/components/filter-renderer";
import { useCommonTableHeader } from "@/modules/common/hooks/common-table.hook/common-table-header.hook";
import { MuiCheckbox } from "@/modules/leads/components/leads-table/components/body/components/checkbox";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const TableHeader: React.FC<TableHeaderProps> = ({
  headerGroups,
  isLoadingFilter,
  filters,
  setFilters,
  tableName,
  initialSortState,
  setInitialSortState,
  zoomLevel,
  setIsTableChange,
  clearMultiselectValues,
  enableRowSelection,
  extraColumnWidth,
  minTableWidth,
  clearSortTrigger,
  getToggleAllRowsSelectedProps,
}) => {
  const { handleSort, getSortIcon } = useCommonTableHeader({
    tableName,
    initialSortState,
    setInitialSortState,
    clearSortTrigger,
    columns: headerGroups[0]?.headers,
  });

  return (
    <TableHead
      sx={{
        display: "flex",
        top: 0,
        zIndex: 9,
        position: "sticky",
        background: "inherit",
        minWidth: `${minTableWidth}px`,
      }}
    >
      {enableRowSelection && (
        <TableCell
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 9,
            backgroundColor: "background.white",
            borderBottom: "1px solid #deebf4",
            boxShadow: "2px 0 2px 0 rgba(0, 0, 0, 0.2)",
            minWidth: 50,
            padding: "0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "var(--dynamic-font-size)",
          }}
        >
          {getToggleAllRowsSelectedProps && (
            <MuiCheckbox {...getToggleAllRowsSelectedProps()} />
          )}
        </TableCell>
      )}

      {headerGroups[0]?.headers.map((column: any, columnIndex: number) => {
        return (
          !column.hideColumn && (
            <TableCell
              key={columnIndex}
              {...column.getHeaderProps()}
              {...(column.isSortable &&
                !column.onSort &&
                column.getHeaderProps(column.getSortByToggleProps()))}
              sx={{
                minWidth: `${column.minWidth + extraColumnWidth}px`,
                backgroundColor:
                  muiTheme.colorSchemes.light.palette.primary[400],
                padding: "0",
                textAlign: "left",
                minHeight: "48px",
                fontWeight: 400,
                display: "flex",
                alignItems: "center",
                color: muiTheme.colorSchemes.light.palette.main.white,
                position: "relative",
                border: "none",
                "&:not(:last-child)": {
                  borderRight: `1px solid ${muiTheme.colorSchemes.light.palette.neutral[200]}`,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  gap: 1.5,
                  padding: "0 14px",
                  flexGrow: 1,
                  minWidth: 0,
                  userSelect: "none",
                  "&:hover": {
                    backgroundColor:
                      muiTheme.colorSchemes.light.palette.primary[500],
                    cursor: "pointer",
                  },
                  ...column?.headerCellStyles,
                }}
                onClick={() => {
                  column.isSortable && handleSort(column);
                }}
              >
                {column.isSortable && (
                  <Box sx={{ flexShrink: 0 }}>{getSortIcon(column)}</Box>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    marginRight: "4px",
                    wordBreak: "keep-all",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                    hyphens: "auto",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    textOverflow: "ellipsis",
                  }}
                >
                  {column.render("Header")}
                  {column.tooltip && (
                    <Tooltip title={column.tooltip} arrow placement="top">
                      <InfoOutlinedIcon
                        sx={{
                          ml: 0.5,
                          fontSize: 16,
                          verticalAlign: "middle",
                          color: "inherit",
                          opacity: 0.7,
                        }}
                      />
                    </Tooltip>
                  )}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <FilterRenderer
                  column={column}
                  isLoadingFilter={isLoadingFilter}
                  filters={filters}
                  setFilters={setFilters}
                  zoomLevel={zoomLevel}
                  setIsTableChange={setIsTableChange}
                  clearMultiselectValues={clearMultiselectValues}
                />
              </Box>
            </TableCell>
          )
        );
      })}
    </TableHead>
  );
};

export default TableHeader;
